import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewModel, editModel, getAllModels } from "../../../redux/features/models/modelsService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { getAllMakes } from "../../../redux/features/makes/makesService";
import TableSkeleton from "../../../components/Skeleton";

const Model = () => {

    const dispatch = useDispatch();
    const makeDropdownRef = useRef(null);
    const makeEditDropdownRef = useRef(null);

    const { isModelsLoading, pageCount, models } = useSelector((state) => state.model.allModels);
    const { isModelCreating } = useSelector((state) => state.model.newModel);
    const { isEditingModel } = useSelector((state) => state.model.editedModel);
    const { makes } = useSelector((state) => state.make.allMakes);

    const [formData, setFormData] = useState({
        name: '',
        makeId: ''
    });

    const [errorMessages, setErrorMessages] = useState({
        name: '',
        makeId: ''
    });
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [initialModel, setInitialModel] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        hs_code: '',
        makeId: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMake, setSelectedMake] = useState({
        id: '',
        name: ''
    });
    const [selectedEditMake, setSelectedEditMake] = useState({
        id: '',
        name: ''
    });


    const fetchAllModels = useCallback(() => {
        dispatch(getAllModels({
            skip: skip,
            limit,
            search: searchQuery
        }));
    }, [dispatch, skip, searchQuery]);

    const fetchAllActiveMakes = useCallback(() => {
        dispatch(getAllMakes({
            status: true,
        }));
    }, [dispatch]);

    useEffect(() => {
        fetchAllModels();
        fetchAllActiveMakes();
    }, [fetchAllModels, fetchAllActiveMakes]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleSelect = (option) => {
        setFormData({ ...formData, makeId: option.id });
        setErrorMessages({ ...errorMessages, makeId: "" });
        setSelectedMake({
            id: option.id,
            name: option.name
        });
    };


    // Validate input
    const isValidModel = () => {
        const errors = {};
        const trimmedName = formData.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!formData.makeId) {
            errors.makeId = "Make is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewModel = async () => {
        if (isValidModel(formData)) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewModel(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                    hs_code: '',
                    makeId: ''
                });
                setSelectedMake({
                    id: '',
                    name: ''
                })
                fetchAllModels();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit model
    const handleSelectedModelChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedModel((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleEditSelect = (option) => {
        setSelectedModel({ ...selectedModel, makeId: option.id });
        setEditErrorMsgs({ ...editErrorMsgs, makeId: "" });
        setSelectedEditMake({
            id: option.id,
            name: option.name
        });
    };

    const isValidEditModel = () => {
        const errors = {};
        const trimmedName = selectedModel.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialModel[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editModelDetails = async () => {
        if (isValidEditModel(selectedModel)) {
            const data = getChangedValues(selectedModel);
            const hasChanges = Object.keys(data).some(key => key !== 'id');

            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editModel(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedModel(null);
                    setInitialModel(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllModels();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate Model
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedModelId, setSelectedModelId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editModel({ id: selectedModelId, status: true })).unwrap();
            setSelectedModelId(null);
            setInitialModel(null);
            showToast('success', response.message);
            fetchAllModels();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editModel({ id: selectedModelId, status: false })).unwrap();
            setSelectedModelId(null);
            setInitialModel(null);
            showToast('success', response.message);
            fetchAllModels();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Pagination
    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Model</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Model Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        HS Code
                                    </label>
                                    <input
                                        type='text'
                                        id='hs_code'
                                        name='hs_code'
                                        value={formData.hs_code}
                                        onChange={handleInputChange}
                                        placeholder='Enter HS Code'
                                        className={`form-input-default`}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Make<span className={`required-star`}>*</span>
                                    </label>
                                    <Dropdown
                                        ref={makeDropdownRef}
                                        options={makes}
                                        placeholder="Choose an Option"
                                        onSelect={handleSelect}
                                        value={selectedMake.name}
                                    />
                                    {errorMessages.makeId && (
                                        <p className={`text-danger`}>{errorMessages.makeId}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewModel}
                        >
                            {isModelCreating ? <BeatLoader model='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Models</h1>
                    <SearchBar onSearch={(query) => setSearchQuery(query)} />
                </div>
                <div className={`search-card-body`}>
                    {isModelsLoading ?
                        <TableSkeleton columns={["Model", "HS Code", "Make Category", "Status", "Actions"]} rowCount={10} height={20} />
                        : models.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Model</th>
                                            <th>HS Code</th>
                                            <th>Make Category</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {models?.map((model) => (
                                            <tr key={model.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {model.name}
                                                </td>
                                                <td>{model.hs_code}</td>
                                                <td>{model.make.name}</td>
                                                <td>{model.status ? "Active" : "Inactive"}</td>
                                                <td className={`actionsBtn`}>
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        setSelectedModel(model);
                                                        setInitialModel(model);
                                                        setSelectedEditMake(model.make);
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            model_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Edit</button>
                                                    <button
                                                        className={model.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedModelId(model.id);
                                                            model.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{model.status ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Model</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='name'
                                                name='name'
                                                value={selectedModel.name || ''}
                                                onChange={handleSelectedModelChange}
                                                placeholder='Enter Model Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                HS Code
                                            </label>
                                            <input
                                                type='text'
                                                id='hs_code'
                                                name='hs_code'
                                                value={selectedModel.hs_code || ''}
                                                onChange={handleSelectedModelChange}
                                                placeholder='Enter HS Code'
                                                className={`form-input-default`}
                                            />
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Make<span className={`required-star`}>*</span>
                                            </label>
                                            <Dropdown
                                                ref={makeEditDropdownRef}
                                                options={makes}
                                                placeholder="Choose an Option"
                                                onSelect={handleEditSelect}
                                                value={selectedEditMake.name}
                                            />
                                            {errorMessages.makeId && (
                                                <p className={`text-danger`}>{errorMessages.makeId}</p>
                                            )}
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editModelDetails}
                                    >
                                        {isEditingModel ? <BeatLoader model='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Model Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Model?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Model Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Model?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Model;
